<script setup lang="ts">
  import dayjs from 'dayjs';
  import { computed, ref } from 'vue';

  import type { Task } from '@stuut/api/models';
  import { AlertDialog } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/tooltip';

  import EmailSignatureEditorDialog from '@/components/email-senders/EmailSignatureEditorDialog.vue';
  import SignatureInfoHoverCard from '@/components/emails/SignatureInfoHoverCard.vue';
  import { useEmailSenders } from '@/store/emailSenders';

  interface Props {
    createdAt?: dayjs.Dayjs | string;
    emailFrom?: string;
    emailSenderId?: string;
    task?: Task;
  }

  const props = defineProps<Props>();

  const emailSendersStore = useEmailSenders();
  const showSignatureDialog = ref(false);
  const cutoff = ref(dayjs.utc('2026-04-16T22:44:00Z'));

  const isCreatedAfterCutoff = computed(() => dayjs(props.createdAt)?.isSameOrAfter(cutoff.value));

  const showSignature = computed(() => {
    if (props.task?.id && !props.task?.isAutoResponseEmail) return true;

    return isCreatedAfterCutoff.value;
  });

  const emailSender = computed(() => {
    if (props.emailFrom) {
      return emailSendersStore.emailSenders.find((sender) => sender.email === props.emailFrom);
    }

    if (props.emailSenderId) {
      return emailSendersStore.getSenderById(props.emailSenderId);
    }

    return undefined;
  });

  const signatureHtml = computed(() => emailSender.value?.signature_html ?? '');
</script>

<template>
  <div
    v-if="showSignature"
    class="flex items-center w-fit gap-x-1.5 py-2 text-muted-foreground group min-h-7"
  >
    <TooltipProvider :skip-delay-duration="250">
      <template v-if="signatureHtml">
        <div class="w-fit text-xs" v-html="signatureHtml" />

        <SignatureInfoHoverCard />

        <Tooltip>
          <TooltipTrigger as-child>
            <Button
              class="opacity-0 group-hover:opacity-100 transition-opacity duration-400"
              variant="ghost"
              size="icon-xs"
              @click.stop.prevent="showSignatureDialog = true"
            >
              <i class="far fa-pen text-xs" />
            </Button>
          </TooltipTrigger>

          <TooltipContent>Edit email signature</TooltipContent>
        </Tooltip>
      </template>

      <template v-else>
        <Tooltip>
          <TooltipTrigger as-child>
            <Button
              class="w-fit px-0"
              variant="transparent"
              size="sm"
              @click.stop.prevent="showSignatureDialog = true"
            >
              <em class="text-xs">No signature</em>

              <i
                v-if="emailSender?.id"
                class="far fa-pen text-xs opacity-0 transition-opacity duration-400 group-hover:opacity-100"
              />
            </Button>
          </TooltipTrigger>

          <TooltipContent v-if="emailSender?.id">Edit email signature</TooltipContent>
        </Tooltip>

        <SignatureInfoHoverCard />
      </template>
    </TooltipProvider>

    <AlertDialog v-if="emailSender" v-model:open="showSignatureDialog">
      <EmailSignatureEditorDialog
        v-if="showSignatureDialog"
        :email-sender="emailSender"
        @close="showSignatureDialog = false"
      />
    </AlertDialog>
  </div>
</template>
